import { toggleToast } from '@bilibili-maoer/prelude'

import { ChangeEventHandler, MouseEventHandler, useState } from 'react'

import { LoadingButton } from '@mui/lab'
import { InputAdornment, InputProps, Skeleton, TextField, useTheme } from '@mui/material'
import useRafLoop from 'react-use/esm/useRafLoop'

import { EObjectiveType } from '~/core/enums'
import { GuildApi } from '~/services/fm/guild'
import { SlideCheckDialog } from '~/shared/components/slide-check-dialog'
import { Seconds } from '~/shared/types/base/units'

interface VerificationCodeInputProps {
  helperText: string | undefined
  loading: boolean
  countdown: Seconds

  slotProps: { input: InputProps }

  onChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>
  onGetCodeClick: MouseEventHandler<HTMLButtonElement>
}

const VerificationCodeInput = (props: VerificationCodeInputProps) => (
  <TextField
    placeholder="请输入验证码"
    error={props.helperText !== undefined}
    helperText={props.helperText}
    InputProps={{
      ...props.slotProps.input,
      onChange: props.onChange,
      endAdornment: (
        <InputAdornment position="end">
          <LoadingButton
            sx={(theme) => ({ color: theme.palette.text.primary, height: '4rem' })}
            loading={props.loading}
            disabled={props.countdown > 0}
            onClick={props.onGetCodeClick}
          >
            {props.countdown ? `${props.countdown}s` : '获取验证码'}
          </LoadingButton>
        </InputAdornment>
      ),
    }}
  />
)

const VerificationCodeInputSkeleton = () => (
  <Skeleton>
    <TextField
      InputProps={{
        endAdornment: <LoadingButton sx={{ height: '4rem' }} />,
      }}
    />
  </Skeleton>
)

export interface VerificationCodeProps {
  helperText: string | undefined

  slotProps: {
    input: InputProps
  }

  onSendCode?: () => void
}

export const VerificationCode = (props: VerificationCodeProps) => {
  const { palette } = useTheme()
  const { data: mobile } = GuildApi.getUserMobile.useQuery()
  const [triggerSendCode, { isLoading, fulfilledTimeStamp }] = GuildApi.sendCode.useMutation()
  const [countdown, setCountdown] = useState(0 as Seconds)
  const [showSlideCheck, setShowSlideCheck] = useState(false)

  useRafLoop(() => {
    setCountdown(
      Math.max(
        0,
        Math.floor((fulfilledTimeStamp ? fulfilledTimeStamp + 60 * 1_000 - Date.now() : 0) / 1_000)
      ) as Seconds
    )
  })

  return (
    <>
      {mobile ? (
        <VerificationCodeInput
          helperText={props.helperText}
          loading={isLoading}
          countdown={countdown}
          slotProps={props.slotProps}
          onChange={(event) => {
            if (/^\d{0,6}$/.test(event.target.value)) {
              props.slotProps.input.onChange?.(event)
            }
          }}
          onGetCodeClick={async () => {
            if (mobile.state === 'not_bind') {
              toggleToast('请先绑定手机号')
              return
            }
            setShowSlideCheck(true)
          }}
        />
      ) : (
        <VerificationCodeInputSkeleton />
      )}

      <SlideCheckDialog
        open={showSlideCheck}
        colorMode={palette.mode}
        onSuccess={async (info) => {
          setShowSlideCheck(false)
          await triggerSendCode({
            data: { post_type: EObjectiveType.ImportantOperation },
            captcha: info.result,
          }).unwrap()
          props.onSendCode?.()
          toggleToast('验证码已发送')
        }}
        onClose={() => {
          setShowSlideCheck(false)
        }}
      />
    </>
  )
}
